import { Suspense, lazy, useRef, useLayoutEffect, useState, useEffect, useCallback } from 'react';
import type { VisualComponent } from '@dtwin/shared';
import { Button, Typography } from 'antd';
import { VideoCameraOutlined, ManOutlined, CompassOutlined } from '@ant-design/icons';
import CompPreviewBox from './CompPreviewBox';
import AiCameraAlertOverlay from './AiCameraAlertOverlay';
import { ensureDeviceSocket } from '../../services/deviceSocket';
import { useRoamStore } from '../../stores/roamStore';
import useSceneStore from '../../stores/sceneStore';

const SceneCanvas = lazy(() => import('../scene/SceneCanvas'));

interface SceneDashboardViewProps {
  sceneId: string;
  components: VisualComponent[];
  pageWidth: number;
  pageHeight: number;
  backgroundColor?: string;
  backgroundImage?: string;
  /** 3D 场景底层（默认不显示：2D 大屏预览/发布默认纯 2D；3D 联动预览显式开启） */
  show3D?: boolean;
  /** 低质量渲染（联动预览等合成视图）：SceneCanvas 关阴影/限像素比，保证页面交互流畅 */
  lowQuality?: boolean;
}

export default function SceneDashboardView({
  sceneId, components, pageWidth, pageHeight, backgroundColor, backgroundImage, show3D = false, lowQuality = false,
}: SceneDashboardViewProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState({ scaleX: 1, scaleY: 1 });

  // 2026-08-26｜展会客户互动：融合展示页相机模式切换（自动漫游环绕 ↔ 第一人称 WASD）
  // 第一人称=客户互动（走进厂房内部）；自动漫游=展台无人时循环播放。切换联动暂停/恢复漫游（防相机抢控）。
  const roamMode = useRoamStore((s) => s.mode);
  const setRoamMode = useRoamStore((s) => s.setMode);
  const autoWalk = useRoamStore((s) => s.autoWalk);
  const setAutoWalk = useRoamStore((s) => s.setAutoWalk);
  const pathPlaying = useSceneStore((s) => s.cameraPathPlaying);
  const setCameraPathPlaying = useSceneStore((s) => s.setCameraPathPlaying);
  const [showTip, setShowTip] = useState(false);
  // 2026-08-26｜自动漫游 toggle：点击开始持续循环漫游（CameraPathPlayer 已改循环），再点击停止
  const toggleOrbit = useCallback(() => {
    if (useSceneStore.getState().cameraPathPlaying) {
      setCameraPathPlaying(false);
    } else {
      setAutoWalk(false);
      setRoamMode('orbit');
      setCameraPathPlaying(true);
    }
    setShowTip(false);
  }, [setCameraPathPlaying, setRoamMode, setAutoWalk]);
  const switchToFirstPerson = useCallback(() => {
    setCameraPathPlaying(false);
    setAutoWalk(false);
    setRoamMode('firstPerson');
    setShowTip(true);
  }, [setRoamMode, setCameraPathPlaying, setAutoWalk]);
  // 2026-08-26｜第一人称自动游览：镜头人视高度 + 沿场景漫游路线自动步行观察厂区（按 WASD 转手动）
  const startAutoWalk = useCallback(() => {
    setCameraPathPlaying(false);
    setRoamMode('firstPerson');
    setAutoWalk(true);
    setShowTip(false);
  }, [setRoamMode, setCameraPathPlaying, setAutoWalk]);

  // 2026-08-26｜自动游览退出兜底：任意 WASD/移动键按下即退出自动游览转手动（组件层监听，
  // 不依赖 useFirstPersonRoam 内部 mode 状态，防止个别构建/环境时序下内部监听未生效）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!useRoamStore.getState().autoWalk) return;
      if (['KeyW', 'KeyS', 'KeyA', 'KeyD', 'Space', 'KeyQ'].includes(e.code)) {
        setAutoWalk(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setAutoWalk]);

  // 2026-08-26｜云端融合展示修复：3D+2D 合成视图挂载即启动设备 WS（3D 动画依赖 deviceDataStore 实时数据；
  // 大屏组件无设备数据源时 useWidgetData 不会触发 ensureDeviceSocket，导致液位/转速/告警全部静默）
  useEffect(() => {
    ensureDeviceSocket();
  }, []);

  // 分轴拉伸（对齐 2D 编辑/预览/发布体系）：scaleX=availW/pageWidth, scaleY=availH/pageHeight，
  // 组件贴边铺满、无空隙、不越界——"同一布局任意宿主表现一致"（阶段0 坐标统一）
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      setScale({ scaleX: width / pageWidth, scaleY: height / pageHeight });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [pageWidth, pageHeight]);

  return (
    <div ref={wrapRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', background: '#000' }}>
      {/* 3D场景层 — 使用 Canvas 渲染（show3D 时开启，默认纯 2D 预览） */}
      {show3D && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          <Suspense fallback={null}>
            {/* 阶段8：alwaysRender——融合展示需持续渲染（液位/转速/告警动画 + 自动漫游） */}
            <SceneCanvas sceneId={sceneId} displayOnly lowQuality={lowQuality} alwaysRender />
          </Suspense>
        </div>
      )}

      {/* 阶段8｜展会客户互动：相机模式切换（自动漫游 / 第一人称 WASD）+ 操作提示。
      位置右下角：右上角被 DashboardEditor「退出联动预览」按钮(zIndex 310)占用，避免遮挡 */}
      {show3D && (
        <div data-testid="camera-mode-bar" style={{ position: 'absolute', bottom: 16, right: 16, zIndex: 310, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, pointerEvents: 'auto' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="small" type={pathPlaying ? 'primary' : 'default'} icon={<VideoCameraOutlined />} onClick={toggleOrbit}>
              自动漫游
            </Button>
            <Button size="small" type={roamMode === 'firstPerson' && !autoWalk ? 'primary' : 'default'} icon={<ManOutlined />} onClick={switchToFirstPerson}>
              第一人称
            </Button>
            <Button size="small" type={autoWalk ? 'primary' : 'default'} icon={<CompassOutlined />} onClick={startAutoWalk}>
              自动游览
            </Button>
          </div>
          {showTip && (
            <Typography.Text style={{ fontSize: 12, color: '#fff', background: 'rgba(0,0,0,0.65)', padding: '4px 10px', borderRadius: 4 }}>
              WASD 移动 · 鼠标转向 · 空格上升 · F 飞行/步行 · ESC 退出
            </Typography.Text>
          )}
        </div>
      )}

      {/* 大屏组件覆盖层 — HTML/CSS 覆盖在 3D 之上，按画布基准缩放铺满，坐标与编辑器一一对应 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          pointerEvents: 'none',
          background: backgroundColor ? `rgba(0,0,0,0)` : undefined,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: pageWidth,
            height: pageHeight,
            transform: `scale(${scale.scaleX}, ${scale.scaleY})`,
            transformOrigin: 'top left',
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
            backgroundSize: 'cover',
          }}
        >
          {components.map((comp) => (
            <CompPreviewBox key={comp.id} comp={comp} interactive />
          ))}
        </div>
      </div>

      {/* M3-M7：AI 检测告警画中画（device:event AI_DETECTION → 弹窗播放相机视频） */}
      <AiCameraAlertOverlay />
    </div>
  );
}
